import { useState } from "react";
import { Plus, Minus, Check } from "lucide-react";
import { Slider } from "../ui/slider";
import { useLang } from "../../lib/i18n";
import { feeAmount } from "../../lib/content";

const dilFee = (p, n) => (n >= p.full_threshold ? p.monthly_fee : n > 0 ? p.reduced_fee : 0);

const Dancer = ({ i, d, onChange, batches, specs, p, cur, fee }) => {
  const { t, lang, pick } = useLang();
  const toggle = (id) => onChange({ ...d, spec: d.spec.includes(id) ? d.spec.filter((x) => x !== id) : [...d.spec, id] });
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid={`calc-dancer-${i}`}>
      <div className="mb-3 flex items-baseline justify-between">
        <span className="font-display text-base font-bold text-white">{t.dancerN(i + 1)}</span>
        <span className="font-mono text-sm text-accent" data-testid={`calc-dancer-${i}-fee`}>{fee} {cur}</span>
      </div>
      <label className="block">
        <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{t.batchLabel}</span>
        <select value={d.batch} onChange={(e) => onChange({ ...d, batch: e.target.value })} data-testid={`calc-dancer-${i}-batch`}
          className="w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
          <option value="">{t.noBatch}</option>
          {batches.map((b) => <option key={b.id} value={b.id}>{`${pick(b, "title")} · ${pick(b, "tag")}`}</option>)}
        </select>
      </label>
      {d.batch && (
        <div className="mt-4">
          <p className="mb-3 text-sm text-zinc-200"><b className="font-mono text-white">{d.n}</b> {t.classesAttended} · <span className="text-accent">{dilFee(p, d.n)} {cur}</span></p>
          <Slider dir={lang === "ar" ? "rtl" : "ltr"} value={[d.n]} min={0} max={Math.max(p.min_classes, 8)} step={1}
            onValueChange={([v]) => onChange({ ...d, n: v })} data-testid={`pricing-calculator-classes-input-${i}`} />
        </div>
      )}
      {specs.length > 0 && (
        <div className="mt-4">
          <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{t.specLabel}</span>
          <div className="flex flex-wrap gap-2">
            {specs.map((s) => {
              const on = d.spec.includes(s.id);
              const amt = feeAmount(s);
              return (
                <button key={s.id} type="button" onClick={() => toggle(s.id)} aria-pressed={on} data-testid={`calc-dancer-${i}-spec-${s.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors ${on ? "border-accent bg-accent text-onaccent" : "border-white/15 text-zinc-200 hover:border-white/40"}`}>
                  {on && <Check size={13} />}{pick(s, "title")} · {amt !== null ? `${amt} ${cur}` : t.askUs}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const Calculator = ({ pricing: p, classes }) => {
  const { t, pick } = useLang();
  const cur = pick(p, "currency");
  const batches = classes.filter((c) => c.program === "dillusions");
  const specs = classes.filter((c) => c.program !== "dillusions");
  const blank = () => ({ batch: batches[0]?.id || "", n: p.min_classes, spec: [] });
  const [dancers, setDancers] = useState(() => [blank()]);

  const valid = (d) => ({ ...d, batch: batches.some((b) => b.id === d.batch) ? d.batch : "", spec: d.spec.filter((id) => specs.some((s) => s.id === id)) });
  const list = dancers.map(valid);
  const fees = list.map((d) => (d.batch ? dilFee(p, d.n) : 0) + d.spec.reduce((sum, id) => sum + (feeAmount(specs.find((s) => s.id === id)) ?? 0), 0));
  const subtotal = fees.reduce((a, b) => a + b, 0);
  const discount = list.filter((d) => d.batch && d.n > 0).length >= 2 ? p.sibling_discount : 0;
  const total = Math.max(subtotal - discount, 0);
  const unpriced = [...new Set(list.flatMap((d) => d.spec))].map((id) => specs.find((s) => s.id === id)).filter((s) => feeAmount(s) === null);
  const setAt = (i, v) => setDancers((ds) => ds.map((x, j) => (j === i ? v : x)));

  return (
    <div className="flex h-full flex-col rounded-2xl border border-accent/30 bg-accent/[0.04] p-5 sm:p-6" data-testid="pricing-calculator" style={{ "--primary": "var(--accent-hsl)", "--background": "var(--accent-hsl)" }}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">{t.calc}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-300">{t.dancers}</span>
          <button onClick={() => setDancers((k) => k.slice(0, -1))} disabled={dancers.length <= 1} data-testid="pricing-calculator-remove-child"
            className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white disabled:opacity-30" aria-label="Remove dancer"><Minus size={14} /></button>
          <span className="w-4 text-center font-mono text-white" data-testid="pricing-calculator-child-count">{dancers.length}</span>
          <button onClick={() => setDancers((k) => [...k, blank()])} disabled={dancers.length >= 5} data-testid="pricing-calculator-sibling-toggle"
            className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white disabled:opacity-30" aria-label="Add dancer"><Plus size={14} /></button>
        </div>
      </div>

      <div className="mt-5 flex-1 space-y-3">
        {list.map((d, i) => <Dancer key={i} i={i} d={d} onChange={(v) => setAt(i, v)} batches={batches} specs={specs} p={p} cur={cur} fee={fees[i]} />)}
        <p className="pt-1 text-sm leading-relaxed text-zinc-400">{t.siblingRule(p.sibling_discount, cur)}</p>
        {unpriced.length > 0 && <p className="text-sm text-zinc-400" data-testid="calc-unpriced-note">{t.priceOnRequest(unpriced.map((s) => pick(s, "title")).join(", "))}</p>}
      </div>

      <div className="mt-6 space-y-1.5 border-t border-white/10 pt-5 text-sm">
        <div className="flex justify-between text-zinc-300"><span>{t.subtotal}</span><span className="font-mono">{subtotal} {cur}</span></div>
        <div className="flex justify-between text-zinc-300"><span>{t.siblingDiscount}</span><span className="font-mono text-accent2">{discount ? `−${discount}` : 0} {cur}</span></div>
        <div className="flex items-end justify-between pt-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400">{t.monthlyTotal}</span>
          <span className="font-display text-5xl font-bold text-white" data-testid="pricing-calculator-total">{total}<span className="ms-1.5 text-lg text-accent">{cur}</span></span>
        </div>
      </div>
    </div>
  );
};
